/**
 * AssessmentDesignRubrics.jsx — Phase 2A-4
 * State machine: DRAFT → APPROVED → PUBLISHED → RETIRED
 * Faculty: create, add criteria. Reviewer: approve. Designer: publish/retire.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, X, ChevronLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell, useIsFaculty, useIsReviewer, useIsDesigner } from "../components/assessment-design/AssessmentDesignShell";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { "Authorization": `Bearer ${localStorage.getItem("academiaos_token")}`, "Content-Type": "application/json" }; }
function apiError(e, fb = "Error") {
  const d = e?.detail || e?.message || fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  return String(d);
}

const RUBRIC_TYPES = ["ANALYTIC","HOLISTIC","SINGLE_POINT"];
const EMPTY_RUBRIC = { code: "", title: "", rubric_type: "ANALYTIC", max_score: "", description: "" };
const EMPTY_CRITERION = { code: "", title: "", max_score: "", weight_pct: "", sequence: 0 };

export default function AssessmentDesignRubrics() {
  const isFaculty = useIsFaculty();
  const isReviewer = useIsReviewer();
  const isDesigner = useIsDesigner();
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_RUBRIC);
  const [saving, setSaving] = useState(false);
  const [showAddCrit, setShowAddCrit] = useState(false);
  const [critForm, setCritForm] = useState(EMPTY_CRITERION);
  const [savingCrit, setSavingCrit] = useState(false);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/assessment/rubrics`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setRubrics(await r.json());
    } catch (e) { toast.error("Failed to load rubrics: " + apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (rub) => {
    setSelected(rub.id);
    try {
      const r = await fetch(`${API}/assessment/rubrics/${rub.id}`, { headers: authH() });
      const data = await r.json();
      if (!r.ok) throw data;
      setSelectedDetail(data);
    } catch (e) {
      toast.error("Failed to load rubric: " + apiError(e));
      setSelectedDetail(rub);
    }
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { code: form.code, title: form.title, rubric_type: form.rubric_type, max_score: parseFloat(form.max_score) };
      if (form.description) body.description = form.description;
      const r = await fetch(`${API}/assessment/rubrics`, { method: "POST", headers: authH(), body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Rubric "${data.title}" created`);
      setShowCreate(false);
      setForm(EMPTY_RUBRIC);
      load();
    } catch (e) { toast.error("Create failed: " + apiError(e)); }
    finally { setSaving(false); }
  };

  const handleAddCriterion = async (e) => {
    e.preventDefault();
    setSavingCrit(true);
    try {
      const body = {
        code: critForm.code, title: critForm.title,
        max_score: parseFloat(critForm.max_score),
        weight_pct: parseFloat(critForm.weight_pct),
        sequence: parseInt(critForm.sequence, 10),
      };
      const r = await fetch(`${API}/assessment/rubrics/${selected}/criteria`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Criterion added");
      setShowAddCrit(false);
      setCritForm(EMPTY_CRITERION);
      openDetail(selectedDetail);
    } catch (e) { toast.error("Add criterion failed: " + apiError(e)); }
    finally { setSavingCrit(false); }
  };

  const handleAction = async (rubric, action) => {
    setActioning(`${rubric.id}-${action}`);
    try {
      const r = await fetch(`${API}/assessment/rubrics/${rubric.id}/${action}`, {
        method: "POST", headers: authH(),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Rubric ${action}d`);
      load();
      if (selected === rubric.id) openDetail(data);
    } catch (e) { toast.error(`${action} failed: ` + apiError(e)); }
    finally { setActioning(null); }
  };

  const criteria = selectedDetail?.criteria ?? [];
  const totalCritWeight = criteria.reduce((s, c) => s + parseFloat(c.weight_pct || 0), 0);

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selected && selectedDetail) {
    const rub = selectedDetail;
    const isImmutable = ["PUBLISHED","RETIRED"].includes(rub.status);
    return (
      <AssessmentDesignShell title={rub.title} breadcrumb="Rubrics">
        <div className="mb-6">
          <button onClick={() => { setSelected(null); setSelectedDetail(null); }}
            data-testid="rubric-back-btn"
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Rubrics
          </button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-100">{rub.title}</h2>
                <LifecycleBadge status={rub.status} />
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span className="font-mono">{rub.code}</span>
                <span>{rub.rubric_type}</span>
                <span>Max: {rub.max_score}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!isImmutable && isFaculty && (
                <button onClick={() => setShowAddCrit(true)} data-testid="add-criterion-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Add Criterion
                </button>
              )}
              {isReviewer && rub.status === "DRAFT" && (
                <button onClick={() => handleAction(rub, "approve")}
                  disabled={actioning === `${rub.id}-approve`}
                  data-testid={`rubric-approve-btn-${rub.id}`}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-700/50 disabled:opacity-50 transition-colors">
                  {actioning === `${rub.id}-approve` ? "…" : "Approve"}
                </button>
              )}
              {isDesigner && rub.status === "APPROVED" && (
                <button onClick={() => handleAction(rub, "publish")}
                  disabled={actioning === `${rub.id}-publish`}
                  data-testid={`rubric-publish-btn-${rub.id}`}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-sky-700 hover:bg-sky-600 text-white disabled:opacity-50 transition-colors">
                  {actioning === `${rub.id}-publish` ? "…" : "Publish"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Criterion weight total */}
        {criteria.length > 0 && (
          <div data-testid="rubric-weight-progress" className="mb-4 rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center justify-between">
            <span className="text-xs text-slate-400">Criterion weights total</span>
            <span className={`text-xs font-mono font-bold ${Math.abs(totalCritWeight - 100) < 0.01 ? "text-emerald-400" : "text-amber-400"}`}>
              {totalCritWeight.toFixed(2)}%
              {Math.abs(totalCritWeight - 100) > 0.01 && <span className="ml-1 text-amber-400 flex items-center gap-1 inline-flex"><AlertTriangle className="w-3 h-3" /></span>}
            </span>
          </div>
        )}

        {/* Criteria */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-slate-300">Criteria ({criteria.length})</h3>
          </div>
          {criteria.length === 0 ? (
            <div data-testid="criteria-empty" className="p-6 text-center text-slate-500 text-sm">
              No criteria yet.{" "}
              {!isImmutable && isFaculty && <button onClick={() => setShowAddCrit(true)} className="text-sky-400 hover:text-sky-300">Add one →</button>}
            </div>
          ) : (
            <div data-testid="criteria-list">
              {criteria.map((c, idx) => (
                <div key={c.id} data-testid={`criterion-row-${c.id}`}
                  className="flex items-center gap-4 px-4 py-3 border-b border-slate-800/50 last:border-0">
                  <span className="w-5 h-5 rounded-full bg-slate-800 flex items-center justify-center text-xs text-slate-400 flex-shrink-0">{c.sequence ?? idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-200">{c.title}</span>
                    <span className="ml-2 text-xs text-slate-500 font-mono">{c.code}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-semibold text-slate-200">{parseFloat(c.weight_pct).toFixed(1)}%</span>
                    <p className="text-xs text-slate-500">{parseFloat(c.max_score).toFixed(0)} pts</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add criterion modal */}
        {showAddCrit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div data-testid="criterion-add-modal" className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
                <h2 className="text-base font-semibold text-slate-100">Add Criterion</h2>
                <button onClick={() => { setShowAddCrit(false); setCritForm(EMPTY_CRITERION); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X className="w-4 h-4" /></button>
              </div>
              <form onSubmit={handleAddCriterion} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                    <input data-testid="criterion-code-input" value={critForm.code}
                      onChange={e => setCritForm(f => ({ ...f, code: e.target.value }))} required
                      placeholder="e.g. C01"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Sequence</label>
                    <input type="number" data-testid="criterion-seq-input" value={critForm.sequence}
                      onChange={e => setCritForm(f => ({ ...f, sequence: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                  <input data-testid="criterion-title-input" value={critForm.title}
                    onChange={e => setCritForm(f => ({ ...f, title: e.target.value }))} required
                    placeholder="Criterion title"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Weight % *</label>
                    <input type="number" step="0.01" data-testid="criterion-weight-input" value={critForm.weight_pct}
                      onChange={e => setCritForm(f => ({ ...f, weight_pct: e.target.value }))} required
                      placeholder="e.g. 30"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Max Score *</label>
                    <input type="number" step="0.01" data-testid="criterion-maxscore-input" value={critForm.max_score}
                      onChange={e => setCritForm(f => ({ ...f, max_score: e.target.value }))} required
                      placeholder="e.g. 20"
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setShowAddCrit(false); setCritForm(EMPTY_CRITERION); }}
                    className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>
                  <button type="submit" disabled={savingCrit} data-testid="criterion-add-submit-btn"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                    {savingCrit ? "Adding…" : "Add Criterion"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AssessmentDesignShell>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <AssessmentDesignShell title="Rubrics" breadcrumb="Rubrics">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">
          Rubrics define scoring criteria. <span className="font-mono text-slate-300">DRAFT → APPROVED → PUBLISHED → RETIRED</span>
        </p>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="rubrics-refresh-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isFaculty && (
            <button onClick={() => setShowCreate(true)} data-testid="rubric-create-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors">
              <Plus className="w-4 h-4" /> New Rubric
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-slate-800/40 animate-pulse" />)}</div>
      ) : rubrics.length === 0 ? (
        <div data-testid="rubrics-empty" className="text-center py-16 text-slate-500 text-sm">
          No rubrics yet.
          {isFaculty && <button onClick={() => setShowCreate(true)} className="block mt-2 text-sky-400 hover:text-sky-300 mx-auto">Create first rubric →</button>}
        </div>
      ) : (
        <div data-testid="rubrics-list" className="space-y-2">
          {rubrics.map(rub => (
            <button key={rub.id} data-testid={`rubric-row-${rub.id}`}
              onClick={() => openDetail(rub)}
              className="w-full text-left flex items-center gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-sky-800 transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-slate-100 group-hover:text-sky-300 transition-colors truncate">{rub.title}</span>
                  <LifecycleBadge status={rub.status} />
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-mono">{rub.code}</span>
                  <span>{rub.rubric_type}</span>
                  <span>Max: {rub.max_score}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isReviewer && rub.status === "DRAFT" && (
                  <button onClick={e => { e.stopPropagation(); handleAction(rub, "approve"); }}
                    disabled={actioning === `${rub.id}-approve`}
                    data-testid={`rubric-list-approve-btn-${rub.id}`}
                    className="px-2.5 py-1 rounded text-xs bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-700/50 disabled:opacity-50 transition-colors">
                    Approve
                  </button>
                )}
                {isDesigner && rub.status === "APPROVED" && (
                  <button onClick={e => { e.stopPropagation(); handleAction(rub, "publish"); }}
                    disabled={actioning === `${rub.id}-publish`}
                    data-testid={`rubric-list-publish-btn-${rub.id}`}
                    className="px-2.5 py-1 rounded text-xs bg-sky-900/40 hover:bg-sky-800/60 text-sky-300 border border-sky-700/50 disabled:opacity-50 transition-colors">
                    Publish
                  </button>
                )}
                <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-500 rotate-180" />
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-testid="rubric-create-modal" className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-base font-semibold text-slate-100">Create Rubric</h2>
              <button onClick={() => { setShowCreate(false); setForm(EMPTY_RUBRIC); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                  <input data-testid="rubric-code-input" value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required
                    placeholder="e.g. RUB-ESSAY-01"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Type</label>
                  <select data-testid="rubric-type-select" value={form.rubric_type}
                    onChange={e => setForm(f => ({ ...f, rubric_type: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                    {RUBRIC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                <input data-testid="rubric-title-input" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
                  placeholder="Rubric title"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Max Score *</label>
                <input type="number" step="0.01" data-testid="rubric-maxscore-input" value={form.max_score}
                  onChange={e => setForm(f => ({ ...f, max_score: e.target.value }))} required
                  placeholder="e.g. 100"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_RUBRIC); }}
                  className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="rubric-create-submit-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                  {saving ? "Creating…" : "Create Rubric"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AssessmentDesignShell>
  );
}
